import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { StowageRulesManager } from './stowageRulesManager';
import { StowageRulesInputs } from 'src/types/ViewModel/Stowage/StowageRule/StowageRulesInputs';
import { StowageRulesOutputs } from 'src/types/ViewModel/Stowage/StowageRule/StowageRulesOutputs';
import { getTankContentForCondition } from '../manifest/tankPlanConditionUtils';
import { TankPlanCondition } from 'src/enums/stowage/tankPlanCondition';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';
import {
    cargoLoadingAndTransferOperationStowageRules,
    cargoTransitOperationsStowageRules,
    cargoOperationStowageRules,
    cargoOperationSpecialCaseStowageRules,
    cargoStowageRules,
    getStowageRules,
    rotationStowageRules,
} from './stowageRulesUtils';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { CoatingCompatibility } from 'src/types/ViewModel/Stowage/Product/CoatingCompatibility';
import { UscgCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/UscgCompatibility/UscgCompatibilitiesBM';
import { StowageDataAccessor } from './stowageDataAccessor';
import { StowageRulesCheckType } from 'src/enums/stowage/stowageRule/stowageRulesCheckType';
import { getBerthCalls, isBefore, isVoyageClosedOrCompleted } from '../stowageRotationUtils';
import { StowageRuleId } from 'src/enums/stowage/stowageRule/stowageRuleId';
import { createAndAddViolation, getViolationId } from '../violations/violationsUtils';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { illegalComminglingHardRule } from './stowageRules/illegalComminglingHardRule';
import { blockedTankMissingCallSoftRule } from './stowageRules/blockedTankMissingCallSoftRule';
import { getOrphanedBlockedTankAnchorBerthCall, hasTankOrphanedBlock } from '../stowageOperation/blockedTankUtils';
import { StowageRuleContext } from 'src/types/ViewModel/Stowage/StowageRule/StowageRuleContext';
import { getNonCommingleableCargoes } from '../stowageOperation/commingleableCargoesUtils';
import { deepCopy } from 'src/utils/objectUtils';
import { getIllegalComminglingTextForViolation } from '../violations/illegalComminglingTextUtils';
import { buildCargoSequenceSnapshots, SequenceSnapshot } from '../manifest/cargoSequenceManifestUtils';
import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { uscgCompatibilityHardRule } from './stowageRules/uscgCompatibilityHardRule';
import { maxAdjacentTemperatureHardRule } from './stowageRules/maxAdjacentTemperatureHardRule';
import { maxAdjacentTemperatureSoftRule } from './stowageRules/maxAdjacentTemperatureSoftRule';
import { assignViolationGroups } from '../violations/violationGroups';

const sequenceSafetyRules = [illegalComminglingHardRule, uscgCompatibilityHardRule, maxAdjacentTemperatureHardRule, maxAdjacentTemperatureSoftRule];
const withoutSequenceSafetyRules = <T extends { id: StowageRuleId }>(rules: T[]): T[] =>
    rules.filter((rule) => !sequenceSafetyRules.some((sequenceRule) => sequenceRule.id === rule.id));

const DEBUG_ENABLED: boolean = false;
const DEFAULT_FIRST_VOYAGE_NUMBER: number = 202401;

const processUscgSequenceDependencies = (
    snapshots: SequenceSnapshot[],
    plan: StowagePlan,
    vessel: Vessel,
    accessor: StowageDataAccessor,
    outputs: StowageRulesOutputs
): void => {
    type DischargeDependency = { content: CargoContent; operations: RotationCargoOperation[]; completed: boolean };
    const discharged = new Map<number, Map<string, DischargeDependency>>();
    const itemsByOperation = new Map<string, typeof plan.stowageItems>();
    for (const item of plan.stowageItems) {
        const items = itemsByOperation.get(item.cargoOperationGuid) ?? [];
        items.push(item);
        itemsByOperation.set(item.cargoOperationGuid, items);
    }
    let berthGuid: string;
    for (const snapshot of snapshots) {
        if (berthGuid !== snapshot.berth.berthCallGuid) {
            discharged.clear();
            berthGuid = snapshot.berth.berthCallGuid;
        }
        for (const operation of snapshot.operations.filter((op) => op.operationType === 'Loading' || op.operationType === 'Transfer')) {
            const loadingTanks = new Set(
                (itemsByOperation.get(operation.cargoOperationGuid) ?? []).filter((item) => item.weight.gt(0)).map((item) => item.tankId)
            );
            for (const tankId of Array.from(loadingTanks)) {
                const tankContent = snapshot.during.get(tankId);
                const incoming = {
                    ...tankContent,
                    cargoContents: tankContent.cargoContents.filter((content) => content.cargo.cargoGuid === operation.cargoGuid),
                };
                for (const { tank: adjacentTank } of tankContent.tank.adjacentTanks ?? []) {
                    const currentAdjacent = snapshot.during.get(adjacentTank.tankId);
                    for (const dependency of Array.from(discharged.get(adjacentTank.tankId)?.values() ?? [])) {
                        const { content, operations } = dependency;
                        if (
                            !dependency.completed ||
                            currentAdjacent.cargoContents.some(
                                (current) => current.cargo.cargoGuid === content.cargo.cargoGuid && current.weight.gt(0)
                            )
                        )
                            continue;
                        const priorAdjacent = { ...currentAdjacent, cargoContents: [content] };
                        if (accessor.areTankContentsUscgCompatible(incoming, priorAdjacent)) continue;
                        const prerequisites = operations.map((unload) => `unloading operation ${unload.cargoOperationSequenceNumber}`).join(', ');
                        createAndAddViolation(
                            StowageRuleId.UscgSequenceDependencySoftRule,
                            {
                                type: StowageRuleConsequenceType.SoftRule,
                                parameter: `USCG safety depends on unloading being completed first. Complete ${prerequisites} for cargo ${
                                    content.cargo.cargoNumber
                                } (${content.cargo.product.tradeName}) from tank ${
                                    adjacentTank.tankName
                                } before starting ${operation.operationType.toLowerCase()} operation ${
                                    operation.cargoOperationSequenceNumber
                                } for cargo ${operation.cargo.cargoNumber} into tank ${
                                    tankContent.tank.tankName
                                }. Simultaneous operations would violate USCG compatibility requirements.`,
                            },
                            {
                                cargoTanks: vessel.cargoTanks,
                                tank: tankContent.tank,
                                tankContent: incoming,
                                cargo: operation.cargo,
                                cargoOperation: operation,
                                berthCall: snapshot.berth,
                                portCall: snapshot.berth.portCall,
                                adjacentTanks: [adjacentTank],
                                applyConsequenceToAlltanks: false,
                                operationTankContents: new Map([
                                    [tankId, incoming],
                                    [adjacentTank.tankId, priorAdjacent],
                                ]),
                            },
                            adjacentTank,
                            outputs
                        );
                    }
                }
            }
        }
        for (const operation of snapshot.operations) {
            for (const item of itemsByOperation.get(operation.cargoOperationGuid) ?? []) {
                if (item.weight.gt(0)) {
                    discharged.get(item.tankId)?.delete(operation.cargoGuid);
                    continue;
                }
                if (operation.operationType !== 'Unloading' || !item.weight.lt(0)) continue;
                const content = snapshot.arrival
                    .get(item.tankId)
                    ?.cargoContents.find((content) => content.cargo.cargoGuid === operation.cargoGuid && content.weight.gt(0));
                if (!content) continue;
                const contents = discharged.get(item.tankId) ?? new Map<string, DischargeDependency>();
                const previous = contents.get(operation.cargoGuid);
                const operations = previous?.operations ?? [];
                const completed =
                    !snapshot.departure
                        .get(item.tankId)
                        ?.cargoContents.some((current) => current.cargo.cargoGuid === operation.cargoGuid && current.weight.gt(0)) &&
                    !snapshot.invalidDischarges.some(
                        (invalid) => invalid.tank.tankId === item.tankId && invalid.operation.cargoGuid === operation.cargoGuid
                    );
                contents.set(operation.cargoGuid, {
                    content,
                    operations: operations.some((previous) => previous.cargoOperationGuid === operation.cargoOperationGuid)
                        ? operations
                        : [...operations, operation],
                    completed,
                });
                discharged.set(item.tankId, contents);
            }
        }
    }
};

const processSequenceSafety = (
    options: StowageRulesOptions,
    manager: StowageRulesManager,
    vessel: Vessel,
    currentPort: RotationPortCall,
    lastPort: RotationPortCall,
    plan: StowagePlan,
    outputs: StowageRulesOutputs,
    accessor: StowageDataAccessor
): void => {
    const firstIndex =
        options.excludePreviousPortCalls && currentPort ? plan.rotation.findIndex((port) => port.portCallGuid === currentPort.portCallGuid) : 0;
    const lastIndex =
        options.excludeAfterLastPortCall && lastPort ? plan.rotation.findIndex((port) => port.portCallGuid === lastPort.portCallGuid) : -1;
    const snapshots =
        plan.manifest?.cargoSequenceSnapshots ??
        buildCargoSequenceSnapshots(plan, vessel.cargoTanks, vessel.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER);
    const rules = getStowageRules(sequenceSafetyRules, !options.excludeSoftRuleViolations, true);
    const actionableSnapshots = snapshots.filter(
        (snapshot) =>
            portCallFilter(plan, snapshot.berth.portCall, firstIndex, lastIndex) && !isVoyageClosedOrCompleted(snapshot.berth.portCall.voyageStatus)
    );
    if (!options.excludeSoftRuleViolations) {
        processUscgSequenceDependencies(actionableSnapshots, plan, vessel, accessor, outputs);
    }
    let evaluationPosition = 0;
    for (const snapshot of actionableSnapshots) {
        for (const { operation, tank } of snapshot.invalidDischarges) {
            createAndAddViolation(
                StowageRuleId.CargoNegativeStowedWeightHardRule,
                {
                    type: StowageRuleConsequenceType.HardRule,
                    parameter: 'Unloading quantity cannot be higher than the remaining quantity in the tank.',
                },
                {
                    cargoTanks: vessel.cargoTanks,
                    tank,
                    cargo: operation.cargo,
                    cargoOperation: operation,
                    berthCall: snapshot.berth,
                    portCall: snapshot.berth.portCall,
                    tankContent: snapshot.arrival.get(tank.tankId),
                    adjacentTanks: [],
                    applyConsequenceToAlltanks: false,
                },
                null,
                outputs
            );
        }
        for (const contents of [snapshot.arrival, snapshot.during, snapshot.departure]) {
            const position = evaluationPosition++;
            const evaluationKey = JSON.stringify([
                snapshot.berth.berthCallGuid,
                snapshot.operations.map((operation) => operation.cargoOperationGuid).sort(),
                contents === snapshot.arrival ? 'arrival' : contents === snapshot.during ? 'during' : 'departure',
            ]);
            for (const tankContent of Array.from(contents.values())) {
                for (const content of tankContent.cargoContents) {
                    const operation =
                        snapshot.berth.cargoOperations.find((item) => item.cargoGuid === content.cargo.cargoGuid) ?? content.cargoOperation;
                    manager.checkStowageRules(
                        {
                            evaluationPosition: position,
                            evaluationKey,
                            stowageRulesCheckType: StowageRulesCheckType.CargoOperationTransit,
                            stowageRules: rules,
                            cargoTanks: vessel.cargoTanks,
                            operationTankContents: contents,
                            tankContent,
                            tank: tankContent.tank,
                            cargo: content.cargo,
                            cargoOperation: operation,
                            portCall: snapshot.berth.portCall,
                            berthCall: snapshot.berth,
                        },
                        outputs
                    );
                }
            }
        }
    }
};

export class StowageRulesOptions {
    excludeSoftRuleViolations: boolean;
    excludePreviousPortCalls: boolean;
    excludeAfterLastPortCall: boolean;
}

export class StowagePlanRulesManager {
    public verifyStowageRules(
        options: StowageRulesOptions,
        stowagePlan: StowagePlan,
        vessel: Vessel,
        currentPortCall: RotationPortCall,
        lastPortCall: RotationPortCall,
        coatingCompatibilities: CoatingCompatibility[],
        uscgCompatibilities: UscgCompatibilitiesBM
    ): Violation[] {
        const dataAccessor = new StowageDataAccessor(stowagePlan, coatingCompatibilities, uscgCompatibilities);
        const stowageRulesOutputs = this.createStowageRulesOutputs();

        if (DEBUG_ENABLED) console.log('Verifying stowage rules...');

        const stowageRulesManager = new StowageRulesManager(options, dataAccessor);
        this.processAllRules(options, stowageRulesManager, vessel, currentPortCall, lastPortCall, stowagePlan, stowageRulesOutputs, dataAccessor);
        assignViolationGroups(stowageRulesOutputs._softRuleViolations, stowagePlan);
        this.removeDuplicateViolations(stowageRulesOutputs);
        this.addComminglingViolationsToLoadingCalls(vessel, stowagePlan, stowageRulesOutputs);
        this.logViolations(stowageRulesOutputs);

        return this.getAllViolations(stowageRulesOutputs);
    }

    private createStowageRulesOutputs(): StowageRulesOutputs {
        return {
            _hardRuleViolations: [],
            _softRuleViolations: [],
            _warnings: [],
        };
    }

    private processAllRules(
        options: StowageRulesOptions,
        stowageRulesManager: StowageRulesManager,
        vessel: Vessel,
        currentPortCall: RotationPortCall,
        lastPortCall: RotationPortCall,
        stowagePlan: StowagePlan,
        outputs: StowageRulesOutputs,
        accessor: StowageDataAccessor
    ): void {
        processRotationRules(options, stowageRulesManager, vessel, currentPortCall, lastPortCall, stowagePlan, outputs);
        processLoadingAndTransferOperationRules(options, stowageRulesManager, vessel, stowagePlan, outputs);
        processCargoOperationRules(options, stowageRulesManager, vessel, stowagePlan, outputs);
        processCargoOperationSpecialCaseRules(options, stowageRulesManager, vessel, stowagePlan, outputs);
        processCargoRules(options, stowageRulesManager, vessel, stowagePlan, outputs);
        processOrphanedBlockedTanks(options, vessel, currentPortCall, stowagePlan, outputs);
        processSequenceSafety(options, stowageRulesManager, vessel, currentPortCall, lastPortCall, stowagePlan, outputs, accessor);
    }

    private removeDuplicateViolations(stowageRulesOutputs: StowageRulesOutputs): void {
        const distinct = (violations: Violation[]) =>
            Array.from(new Map(violations.map((violation) => [JSON.stringify([getViolationId(violation), violation.groupKey]), violation])).values());
        stowageRulesOutputs._hardRuleViolations = distinct(stowageRulesOutputs._hardRuleViolations);
        stowageRulesOutputs._softRuleViolations = distinct(stowageRulesOutputs._softRuleViolations);
        stowageRulesOutputs._warnings = distinct(stowageRulesOutputs._warnings);
        stowageRulesOutputs._softRuleViolations = stowageRulesOutputs._softRuleViolations.filter(
            (soft) =>
                !stowageRulesOutputs._hardRuleViolations.some(
                    (hard) =>
                        soft.description === hard.description &&
                        soft.portCall?.portCallGuid === hard.portCall?.portCallGuid &&
                        soft.berthCall?.berthCallGuid === hard.berthCall?.berthCallGuid &&
                        soft.cargoOperation?.cargoOperationGuid === hard.cargoOperation?.cargoOperationGuid &&
                        soft.cargo?.cargoGuid === hard.cargo?.cargoGuid &&
                        soft.tank?.tankId === hard.tank?.tankId &&
                        soft.adjacentTank?.tankId === hard.adjacentTank?.tankId
                )
        );
    }

    private logViolations(stowageRulesOutputs: StowageRulesOutputs): void {
        if (!DEBUG_ENABLED) return;

        if (this.hasNoViolations(stowageRulesOutputs)) {
            console.log('No violations detected.');
            return;
        }

        if (stowageRulesOutputs._hardRuleViolations.length > 0) {
            console.log('Hard rule violations detected:', stowageRulesOutputs._hardRuleViolations);
        }
        if (stowageRulesOutputs._softRuleViolations.length > 0) {
            console.log('Soft rule violations detected:', stowageRulesOutputs._softRuleViolations);
        }
        if (stowageRulesOutputs._warnings.length > 0) {
            console.log('Warnings detected:', stowageRulesOutputs._warnings);
        }
    }

    private hasNoViolations(stowageRulesOutputs: StowageRulesOutputs): boolean {
        return (
            stowageRulesOutputs._hardRuleViolations.length === 0 &&
            stowageRulesOutputs._softRuleViolations.length === 0 &&
            stowageRulesOutputs._warnings.length === 0
        );
    }

    private getAllViolations(stowageRulesOutputs: StowageRulesOutputs): Violation[] {
        return [...stowageRulesOutputs._hardRuleViolations, ...stowageRulesOutputs._softRuleViolations, ...stowageRulesOutputs._warnings];
    }

    private addComminglingViolationsToLoadingCalls(vessel: Vessel, stowagePlan: StowagePlan, stowageRulesOutputs: StowageRulesOutputs): void {
        const illegalComminglingViolations = stowageRulesOutputs._hardRuleViolations.filter(
            (violation) => violation.stowageRuleId === StowageRuleId.IllegalComminglingHardRule
        );

        for (const violation of illegalComminglingViolations) {
            if (violation.sequenceConflictKey) continue;
            if (!violation.tank || !violation.portCall || !violation.berthCall || !violation.cargo) {
                continue;
            }

            const tankContent = getTankContentForCondition(
                stowagePlan,
                violation.portCall,
                violation.berthCall,
                TankPlanCondition.Departure,
                violation.tank
            );

            if (!tankContent || tankContent.cargoContents.length === 0) {
                continue;
            }

            this.processNonCommingleableCargoes(vessel, stowagePlan, violation, tankContent, stowageRulesOutputs);
        }
    }

    private processNonCommingleableCargoes(
        vessel: Vessel,
        stowagePlan: StowagePlan,
        violation: Violation,
        tankContent: any,
        stowageRulesOutputs: StowageRulesOutputs
    ): void {
        const nonCommingleableCargoes = getNonCommingleableCargoes(tankContent.cargoContents);

        for (const cargoContent of nonCommingleableCargoes) {
            if (violation.cargoGuid !== cargoContent.cargo.cargoGuid) {
                this.addViolationForCargoContent(vessel, stowagePlan, violation.tank, cargoContent.cargo, stowageRulesOutputs, violation);
            }
        }
    }

    private addViolationForCargoContent(
        vessel: Vessel,
        stowagePlan: StowagePlan,
        tank: any,
        cargo: any,
        stowageRulesOutputs: StowageRulesOutputs,
        violation: Violation
    ): void {
        const stowageItems = stowagePlan.stowageItems.filter(
            (item) => item.cargoOperation.cargoGuid === cargo.cargoGuid && item.tankId === tank.tankId
        );

        if (stowageItems.length === 0) {
            return;
        }

        const sortedStowageItems = stowageItems.sort((a, b) =>
            isBefore(stowagePlan.berthCalls, a.cargoOperation.berthCall, b.cargoOperation.berthCall) ? -1 : 1
        );

        const loadingCargoOperation = sortedStowageItems[0].cargoOperation;
        const firstVoyage = vessel.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER;
        if (Number(loadingCargoOperation.berthCall.portCall.voyageNumber) >= firstVoyage) {
            const _illegalComminglingHardRule = deepCopy(illegalComminglingHardRule);
            _illegalComminglingHardRule.consequence.parameter = getIllegalComminglingTextForViolation(
                _illegalComminglingHardRule.consequence.parameter,
                violation
            );
            createAndAddViolation(
                StowageRuleId.IllegalComminglingHardRule,
                _illegalComminglingHardRule.consequence,
                {
                    portCall: loadingCargoOperation.berthCall.portCall,
                    berthCall: loadingCargoOperation.berthCall,
                    cargo: cargo,
                    cargoOperation: loadingCargoOperation,
                    tank: tank,
                } as StowageRuleContext,
                null,
                stowageRulesOutputs
            );
        }
    }
}

/**
 * Filters port calls based on first and last port call indexes.
 */
const portCallFilter = (stowagePlan: StowagePlan, portCall: RotationPortCall, firstPortCallIndex: number, lastPortCallIndex: number): boolean => {
    return (
        (firstPortCallIndex === 0 || stowagePlan.rotation.findIndex((p) => p.portCallGuid === portCall.portCallGuid) >= firstPortCallIndex) &&
        (lastPortCallIndex === -1 || stowagePlan.rotation.findIndex((p) => p.portCallGuid === portCall.portCallGuid) <= lastPortCallIndex)
    );
};

const processRotationRules = (
    options: StowageRulesOptions,
    stowageRulesManager: StowageRulesManager,
    vessel: Vessel,
    currentPortCall: RotationPortCall,
    lastPortCall: RotationPortCall | null,
    stowagePlan: StowagePlan,
    stowageRulesOutputs: StowageRulesOutputs
) => {
    const firstPortCallIndex =
        options.excludePreviousPortCalls && currentPortCall
            ? stowagePlan.rotation.findIndex((p) => p.portCallGuid === currentPortCall.portCallGuid)
            : 0;

    const lastPortCallIndex =
        options.excludeAfterLastPortCall && lastPortCall ? stowagePlan.rotation.findIndex((p) => p.portCallGuid === lastPortCall.portCallGuid) : -1;

    const firstVoyage = vessel.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER;

    stowagePlan.rotation
        .filter((o) => Number(o.voyageNumber) >= firstVoyage)
        .filter((portCall) => portCallFilter(stowagePlan, portCall, firstPortCallIndex, lastPortCallIndex))
        .forEach((portCall) => {
            getBerthCalls(portCall).forEach((berthCall) => {
                vessel.cargoTanks.forEach((tank) => {
                    const stowageRulesInputs: StowageRulesInputs = {
                        stowageRulesCheckType: StowageRulesCheckType.Rotation,
                        stowageRules: getStowageRules(withoutSequenceSafetyRules(rotationStowageRules), !options.excludeSoftRuleViolations, true),
                        cargoTanks: vessel.cargoTanks,
                        tankContent: getTankContentForCondition(stowagePlan, portCall, berthCall, TankPlanCondition.Departure, tank),
                        tank: tank,
                        portCall: portCall,
                        berthCall: berthCall,
                    } as StowageRulesInputs;
                    stowageRulesManager.checkStowageRules(stowageRulesInputs, stowageRulesOutputs);
                });
            });
        });
};

const processOrphanedBlockedTanks = (
    options: StowageRulesOptions,
    vessel: Vessel,
    currentPortCall: RotationPortCall | null,
    stowagePlan: StowagePlan,
    stowageRulesOutputs: StowageRulesOutputs
) => {
    if (options.excludeSoftRuleViolations) return;

    const orphanedTanks = vessel.cargoTanks.filter((tank) => hasTankOrphanedBlock(stowagePlan, tank, stowagePlan.rotation));
    if (orphanedTanks.length === 0) return;

    const anchorBerthCall = getOrphanedBlockedTankAnchorBerthCall(stowagePlan.rotation, currentPortCall);
    if (!anchorBerthCall) return;

    orphanedTanks.forEach((tank) => {
        createAndAddViolation(
            StowageRuleId.BlockedTankMissingCallSoftRule,
            blockedTankMissingCallSoftRule.consequence,
            { portCall: anchorBerthCall.portCall, berthCall: anchorBerthCall, tank } as StowageRuleContext,
            null,
            stowageRulesOutputs
        );
    });
};

const processLoadingAndTransferOperationRules = (
    options: StowageRulesOptions,
    stowageRulesManager: StowageRulesManager,
    vessel: Vessel,
    stowagePlan: StowagePlan,
    stowageRulesOutputs: StowageRulesOutputs
) => {
    const firstVoyage = vessel.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER;

    // Extract transit operations from stowagePlan.transitOperations
    const transitOperations = (stowagePlan.transitOperations ?? [])
        .flatMap((portCall) => portCall.berthCalls ?? [])
        .flatMap((berthCall) => berthCall.cargoOperations ?? [])
        .filter((o) => Number(o.cargo.voyageNumber) >= firstVoyage);

    // Pre-filter cargo operations once - now including transit operations
    const filteredOperations = [
        ...stowagePlan.cargoOperations.filter(
            (o) => Number(o.cargo.voyageNumber) >= firstVoyage && (o.operationType === 'Loading' || o.operationType === 'Transfer')
        ),
        ...transitOperations,
    ];

    // Get rules for loading/transfer operations
    const loadingTransferRules = getStowageRules(
        withoutSequenceSafetyRules(cargoLoadingAndTransferOperationStowageRules),
        !options.excludeSoftRuleViolations,
        true
    );

    // Get rules for transit operations (only USCG compatibility)
    const transitRules = getStowageRules(cargoTransitOperationsStowageRules, !options.excludeSoftRuleViolations, true);

    // Cache cargo tanks array length
    const tanks = vessel.cargoTanks;

    for (const cargoOperation of filteredOperations) {
        const berthCall = cargoOperation.berthCall;
        const { cargo } = cargoOperation;

        // Determine which rule set to use based on operation type
        const stowageRules = cargoOperation.operationType === 'Transit' ? transitRules : loadingTransferRules;

        // Use CargoOperationTransit check type for transit operations to exclude product rules
        const checkType =
            cargoOperation.operationType === 'Transit' ? StowageRulesCheckType.CargoOperationTransit : StowageRulesCheckType.CargoOperation;

        // Create base stowage rules inputs object
        const baseStowageRulesInputs: Partial<StowageRulesInputs> = {
            stowageRulesCheckType: checkType,
            stowageRules,
            cargoTanks: tanks,
        };

        // Use for...of for better performance with arrays
        for (const tank of tanks) {
            const tankContent = getTankContentForCondition(stowagePlan, berthCall.portCall, berthCall, TankPlanCondition.Departure, tank);

            // Early continue with simplified condition
            if (!tankContent.cargoContents.some((c) => c.cargo.cargoGuid === cargo.cargoGuid)) {
                continue;
            }

            // Spread operator is faster than Object.assign for shallow copies
            const stowageRulesInputs: StowageRulesInputs = {
                ...baseStowageRulesInputs,
                tankContent,
                tank,
                cargo,
                cargoOperation,
                portCall: berthCall.portCall,
                berthCall,
            } as StowageRulesInputs;

            stowageRulesManager.checkStowageRules(stowageRulesInputs, stowageRulesOutputs);
        }
    }
};

const processCargoOperationRules = (
    options: StowageRulesOptions,
    stowageRulesManager: StowageRulesManager,
    vessel: Vessel,
    stowagePlan: StowagePlan,
    stowageRulesOutputs: StowageRulesOutputs
) => {
    const firstVoyage = vessel.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER;
    // Pre-filter cargo operations once
    const filteredOperations = stowagePlan.cargoOperations.filter((o) => Number(o.cargo.voyageNumber) >= firstVoyage);

    // Get rules once outside the loop
    const stowageRules = getStowageRules(cargoOperationStowageRules, !options.excludeSoftRuleViolations, true);

    // Create base stowage rules inputs object outside loop
    const baseStowageRulesInputs: Partial<StowageRulesInputs> = {
        stowageRulesCheckType: StowageRulesCheckType.CargoOperation,
        stowageRules,
        cargoTanks: vessel.cargoTanks,
    };

    // Use for...of for better performance with arrays
    for (const cargoOperation of filteredOperations) {
        const { cargo, berthCall } = cargoOperation;

        // Spread operator is faster than Object.assign for shallow copies
        const stowageRulesInputs: StowageRulesInputs = {
            ...baseStowageRulesInputs,
            cargo,
            cargoOperation,
            portCall: berthCall.portCall,
            berthCall,
        } as StowageRulesInputs;

        stowageRulesManager.checkStowageRules(stowageRulesInputs, stowageRulesOutputs);
    }
};

const processCargoOperationSpecialCaseRules = (
    options: StowageRulesOptions,
    stowageRulesManager: StowageRulesManager,
    vessel: Vessel,
    stowagePlan: StowagePlan,
    stowageRulesOutputs: StowageRulesOutputs
) => {
    // No first voyage filtering; gating handled inside rule condition (e.g., voyage status Scheduled)
    const stowageRules = getStowageRules(cargoOperationSpecialCaseStowageRules, !options.excludeSoftRuleViolations, true);
    const baseInputs: Partial<StowageRulesInputs> = {
        stowageRulesCheckType: StowageRulesCheckType.CargoOperation,
        stowageRules,
        cargoTanks: vessel.cargoTanks,
    };
    for (const cargoOperation of stowagePlan.cargoOperations) {
        const { cargo, berthCall } = cargoOperation;
        const inputs: StowageRulesInputs = {
            ...baseInputs,
            cargo,
            cargoOperation,
            portCall: berthCall.portCall,
            berthCall,
        } as StowageRulesInputs;
        stowageRulesManager.checkStowageRules(inputs, stowageRulesOutputs);
    }
};

const processCargoRules = (
    options: StowageRulesOptions,
    stowageRulesManager: StowageRulesManager,
    vessel: Vessel,
    stowagePlan: StowagePlan,
    stowageRulesOutputs: StowageRulesOutputs
) => {
    const firstVoyage = vessel.firstVoyageOrcaWebStowageViolations ?? DEFAULT_FIRST_VOYAGE_NUMBER;

    const stowageRules = getStowageRules(cargoStowageRules, !options.excludeSoftRuleViolations, true);

    const baseStowageRulesInputs: Partial<StowageRulesInputs> = {
        stowageRulesCheckType: StowageRulesCheckType.Cargo,
        stowageRules,
        cargoTanks: vessel.cargoTanks,
    };

    const filteredCargoes = stowagePlan.cargoes.filter((o) => Number(o.voyageNumber) >= firstVoyage);

    const originalViolationsCount = {
        hard: stowageRulesOutputs._hardRuleViolations.length,
        soft: stowageRulesOutputs._softRuleViolations.length,
        warnings: stowageRulesOutputs._warnings.length,
    };

    for (const cargo of filteredCargoes) {
        const stowageRulesInputs: StowageRulesInputs = {
            ...baseStowageRulesInputs,
            cargo,
        } as StowageRulesInputs;

        stowageRulesManager.checkStowageRules(stowageRulesInputs, stowageRulesOutputs);
    }

    const newHardViolations = stowageRulesOutputs._hardRuleViolations.slice(originalViolationsCount.hard);
    const newSoftViolations = stowageRulesOutputs._softRuleViolations.slice(originalViolationsCount.soft);
    const newWarnings = stowageRulesOutputs._warnings.slice(originalViolationsCount.warnings);

    // Remove the cargo-level violations we just added (we'll replace them with cargo-operation-level violations)
    stowageRulesOutputs._hardRuleViolations.length = originalViolationsCount.hard;
    stowageRulesOutputs._softRuleViolations.length = originalViolationsCount.soft;
    stowageRulesOutputs._warnings.length = originalViolationsCount.warnings;

    const processedCargoes = new Set<string>();

    const processViolations = (violations: Violation[], targetArray: Violation[]) => {
        for (const violation of violations) {
            if (!violation.cargo?.cargoGuid) continue;

            const cargoGuid = violation.cargo.cargoGuid;
            if (processedCargoes.has(cargoGuid)) continue;
            processedCargoes.add(cargoGuid);

            const cargoOperations = stowagePlan.cargoOperations.filter((op) => op.cargo.cargoGuid === cargoGuid);

            if (cargoOperations.length) {
                for (const cargoOperation of cargoOperations) {
                    const clonedViolation = { ...violation };
                    clonedViolation.violationGuid = crypto.randomUUID();
                    clonedViolation.berthCall = cargoOperation.berthCall;
                    clonedViolation.berthCallGuid = cargoOperation.berthCall?.berthCallGuid;
                    clonedViolation.portCall = cargoOperation.berthCall?.portCall;
                    clonedViolation.portCallGuid = cargoOperation.berthCall?.portCall?.portCallGuid;
                    clonedViolation.cargoOperation = cargoOperation;
                    clonedViolation.cargoOperationGuid = cargoOperation.cargoOperationGuid;

                    targetArray.push(clonedViolation);
                }
            }
        }
    };

    processViolations(newHardViolations, stowageRulesOutputs._hardRuleViolations);
    processViolations(newSoftViolations, stowageRulesOutputs._softRuleViolations);
    processViolations(newWarnings, stowageRulesOutputs._warnings);
};
